/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isObject from '@stdlib/assert-is-plain-object';
import hasOwnProp from '@stdlib/assert-has-own-property';
import contains from '@stdlib/array-base-assert-contains';
import join from '@stdlib/array-base-join';
import resolveStr from '@stdlib/ndarray-base-dtype-resolve-str';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination object
* @param {Array<string>} dtypes - list of supported output data types
* @param {Options} options - function options
* @param {*} [options.dtype] - array data type
* @param {string} [options.order] - memory layout (either row-major or column-major)
* @param {string} [options.mode] - specifies how to handle indices which exceed array dimensions
* @param {ArrayLikeObject<string>} [options.submode] - specifies how to handle subscripts which exceed array dimensions on a per dimension basis
* @param {boolean} [options.readonly] - boolean indicating whether an array should be read-only
* @returns {(Error|null)} null or an error object
*
* @example
* var opts = {};
* var dtypes = [ 'float64', 'float32', 'generic' ];
*
* var options = {
*     'dtype': 'float64'
* };
* var err = validate( opts, dtypes, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, dtypes, options ) {
	if ( !isObject( options ) ) {
		return new TypeError( format( '2HH2V', options ) );
	}
	if ( hasOwnProp( options, 'dtype' ) ) {
		opts.dtype = resolveStr( options.dtype );
		if ( !contains( dtypes, opts.dtype ) ) {
			return new TypeError( format( '2HH4S', 'dtype', join( dtypes, '", "' ), options.dtype ) );
		}
	}
	// Pass-through options...
	if ( hasOwnProp( options, 'order' ) ) {
		opts.order = options.order;
	}
	if ( hasOwnProp( options, 'mode' ) ) {
		opts.mode = options.mode;
	}
	if ( hasOwnProp( options, 'submode' ) ) {
		opts.submode = options.submode;
	}
	if ( hasOwnProp( options, 'readonly' ) ) {
		opts.readonly = options.readonly;
	}
	return null;
}


// EXPORTS //

export default validate;
